import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import RoutingStrip from "../../components/RoutingStrip.jsx";
import StickyActionBar from "../ui/StickyActionBar.jsx";
import PhotoButton from "../ui/PhotoButton.jsx";
import { StatusPill } from "./queue/QueueTaskCard.jsx";
import { AddFilePanel, JobEditPanel, PreShipPanel, TaskVideoPanel } from "./jobs/JobPanels.jsx";
import { jobRouting } from "./routing";
import { JOB_PRIORITY_OPTIONS, canPreShip, loadJob, loadJobDocuments, loadJobTasks, relativeDueLabel } from "../../features/jobs/jobsData";
import { loadJobParts } from "../../features/queue/queueData";
import { canShowTaskVideo, loadCameraConfig } from "../../features/jobs/taskVideo";
import "../mobile.css";

// The phone job detail (/jobs/:id, plan Phase 1 Jobs): status, due date, priority, routing, tasks
// (with camera footage of finished ones), parts with pictures, the job's files, notes. Edit (status,
// priority, due date, notes), Pre-ship (PIN) and Add a photo / file (PIN). Cancel, delete and Ship
// stay on the desktop. Layout only: reads and actions are features/jobs/ (+ the queue's part
// pictures, features/queue/queueData.loadJobParts).

const TASKS_SHOWN = 12;
const PRIORITY = Object.fromEntries(JOB_PRIORITY_OPTIONS.map((p) => [p.value, p.label]));
const fileIcon = (name) => {
  const ext = String(name || "").split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "heic", "webp"].includes(ext)) return "fa-file-image";
  if (ext === "pdf") return "fa-file-pdf";
  if (["dxf", "dwg", "step", "stp"].includes(ext)) return "fa-drafting-compass";
  return "fa-file";
};

export default function JobDetailScreen() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [parts, setParts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [cameraConfig, setCameraConfig] = useState(null);
  const [error, setError] = useState(null);
  const [panel, setPanel] = useState(null); // {kind: edit | preship | file | video, ...}
  const [allTasks, setAllTasks] = useState(false); // a set-built job can have 50+ tasks
  const fileInput = useRef(null);
  const loadSeq = useRef(0);

  // Another job (the same screen, a new :jobId): start clean, never show the last one's lists.
  useEffect(() => {
    setJob(null);
    setTasks([]);
    setParts([]);
    setDocs([]);
    setError(null);
    setPanel(null);
    setAllTasks(false);
  }, [jobId]);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const fresh = () => seq === loadSeq.current;
    try {
      const j = await loadJob(jobId);
      if (!fresh()) return;
      setJob(j);
      setError(null);
    } catch (err) {
      if (fresh()) setError(err.message || "Couldn't load this job.");
      return;
    }
    loadJobTasks(jobId).then((t) => { if (fresh() && t) setTasks(t); }).catch((err) => console.error("Error fetching tasks:", err));
    loadJobParts(jobId).then((p) => { if (fresh() && p) setParts(p); }).catch((err) => console.warn("Job parts did not load:", err));
    loadJobDocuments(jobId).then((d) => { if (fresh() && d) setDocs(d); }).catch((err) => console.error("Failed to load documents", err));
  }, [jobId]);

  useEffect(() => {
    load();
    return () => { loadSeq.current += 1; };
  }, [load]);

  useEffect(() => {
    loadCameraConfig().then((c) => { if (c) setCameraConfig(c); });
  }, []);

  if (error && !job) {
    return (
      <div className="jq-screen" data-testid="phone-job-detail">
        <div className="jq-note jq-note-danger" role="alert">{error}</div>
        <button type="button" className="btn btn-outline-primary btn-lg w-100" onClick={load}>Try again</button>
      </div>
    );
  }
  if (!job) {
    return (
      <div className="jq-screen jq-list-state" role="status" data-testid="phone-job-detail">
        <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />Loading job…
      </div>
    );
  }

  const due = relativeDueLabel(job.due_date);
  const closed = ["Completed", "Shipped", "Cancelled"].includes(job.status);
  const customer = (tasks.find((t) => t.customer_name) || {}).customer_name;
  const routing = jobRouting(tasks);
  const sortedTasks = [...tasks].sort((a, b) => a.task_id - b.task_id);
  const preShipOk = canPreShip(job);
  const closePanel = () => setPanel(null);
  // A save that finishes after its panel was closed must not close another one.
  const closeIf = (kind) => setPanel((p) => (p && p.kind === kind ? null : p));

  return (
    <div className="jq-screen jq-has-action-bar" data-testid="phone-job-detail">
      <div className="jq-job-head">
        <div className="jq-qcard-top">
          <span className="jq-qcard-job">Job #{job.master_job_num || job.job_id}</span>
          <StatusPill status={job.status} />
          {job.priority_level === 1 && <span className="jq-tag jq-tag-block">P1</span>}
        </div>
        <div className="jq-qcard-title">{job.job_description}</div>
      </div>

      <dl className="jq-kv">
        {customer && <div><dt>Customer</dt><dd>{customer}</dd></div>}
        <div>
          <dt>Due</dt>
          <dd>
            {job.due_date ? String(job.due_date).split("T")[0] : "—"}
            {due && !closed && <div style={{ color: due.color, fontSize: "0.8rem" }}>{due.text}</div>}
          </dd>
        </div>
        <div><dt>Priority</dt><dd>{PRIORITY[job.priority_level] || job.priority_level || "—"}</dd></div>
        {job.quantity_ordered > 0 && <div><dt>Done</dt><dd>{job.qty_completed || 0} of {job.quantity_ordered}</dd></div>}
        {job.sales_order_number && <div><dt>Order</dt><dd>{job.sales_order_number}</dd></div>}
        {job.customer_po && <div><dt>Customer PO</dt><dd>{job.customer_po}</dd></div>}
        {job.shipping_method && <div><dt>Shipping</dt><dd>{job.shipping_method}</dd></div>}
      </dl>

      {error && <div className="jq-note jq-note-danger" role="alert">{error}</div>}
      {job.notes && <div className="jq-note"><strong>Notes</strong>{"\n"}{job.notes}</div>}

      {(routing.shared.length > 0 || routing.parts.length > 0) && (
        <section className="jq-block" aria-label="Routing">
          <h3>Routing</h3>
          {routing.shared.length > 0 && <div className="mb-2"><RoutingStrip steps={routing.shared} ariaLabel="Shared sheet" /></div>}
          {routing.parts.map((g) => (
            <div key={g.partId} className="mb-2">
              <div className="jq-muted fw-semibold">{g.productId}</div>
              <RoutingStrip steps={g.steps} ariaLabel={`Routing for ${g.productId}`} />
            </div>
          ))}
        </section>
      )}

      {sortedTasks.length > 0 && (
        <section className="jq-block" aria-label="Tasks">
          <h3>Tasks ({sortedTasks.length})</h3>
          {(allTasks ? sortedTasks : sortedTasks.slice(0, TASKS_SHOWN)).map((t) => (
            <div key={t.task_id} className="jq-task-line">
              <div className="jq-task-line-main">
                <div className="fw-semibold">{t.workstation}{t.sub_process ? ` · ${t.sub_process}` : ""}</div>
                <div className="jq-muted">Task {t.task_id}{t.product_id ? ` · ${t.product_id}` : ""} · qty {t.quantity}</div>
              </div>
              <StatusPill status={t.status} />
              {canShowTaskVideo(t, cameraConfig) && (
                <button type="button" className="btn btn-outline-dark" aria-label={`Video of task ${t.task_id}`} onClick={() => setPanel({ kind: "video", task: t })}>
                  <i className="fas fa-video" aria-hidden="true" />
                </button>
              )}
            </div>
          ))}
          {!allTasks && sortedTasks.length > TASKS_SHOWN && (
            <button type="button" className="btn btn-outline-secondary jq-show-more mt-2" onClick={() => setAllTasks(true)}>
              Show all {sortedTasks.length} tasks
            </button>
          )}
        </section>
      )}

      {parts.length > 0 && (
        <section className="jq-block" aria-label="Parts">
          <h3>Parts ({parts.length})</h3>
          {parts.map((p) => (
            <div key={p.job_part_id || p.part_id} className="jq-part">
              {p.image_path ? <img className="jq-part-img" src={p.image_path} alt="" loading="lazy" /> : null}
              <div className="jq-part-body">
                <div className="fw-semibold">{p.product_id}</div>
                {p.description && <div className="jq-muted">{p.description}</div>}
                <div className="jq-part-row-meta">{p.quantity_ordered} ordered · {p.quantity_shipped || 0} shipped</div>
                {p.flagged && <div className="text-danger small">⚑ {p.flag_reason || "Flagged"}</div>}
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="jq-block" aria-label="Files">
        <h3>Files ({docs.length})</h3>
        {docs.length === 0 && <div className="jq-muted mb-2">No files on this job.</div>}
        {docs.map((d) => (
          <a key={d.document_id} className="jq-file" href={d.s3_url} target="_blank" rel="noreferrer">
            <i className={`fas ${fileIcon(d.original_filename)}`} aria-hidden="true" />
            <div className="jq-file-body">
              <div className="jq-file-name">{d.display_name}</div>
              <div className="jq-muted">{[d.description, d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : null].filter(Boolean).join(" · ")}</div>
            </div>
          </a>
        ))}
        <div className="jq-two-buttons mt-2">
          <PhotoButton label="Photo" className="btn btn-outline-primary btn-lg" onPick={(f) => setPanel({ kind: "file", file: f, isPhoto: true })} />
          <button type="button" className="btn btn-outline-primary btn-lg" onClick={() => fileInput.current && fileInput.current.click()}>
            <i className="fas fa-paperclip me-2" aria-hidden="true" />File
          </button>
        </div>
        <input
          ref={fileInput}
          type="file"
          className="jq-visually-hidden"
          aria-label="Choose a file to add"
          onChange={(e) => {
            const f = e.target.files && e.target.files[0];
            e.target.value = "";
            if (f) setPanel({ kind: "file", file: f, isPhoto: false });
          }}
        />
      </section>

      <StickyActionBar>
        <button type="button" className="btn btn-outline-primary btn-lg" onClick={() => setPanel({ kind: "edit" })}>Edit</button>
        {preShipOk && <button type="button" className="btn btn-primary btn-lg" onClick={() => setPanel({ kind: "preship" })}>Pre-ship</button>}
      </StickyActionBar>

      {panel && panel.kind === "edit" && (
        <JobEditPanel job={job} onClose={closePanel} onSaved={(patch) => { setJob((j) => ({ ...j, ...patch })); closeIf("edit"); load(); }} />
      )}
      {panel && panel.kind === "preship" && <PreShipPanel job={job} onClose={closePanel} onDone={() => { closeIf("preship"); load(); }} />}
      {panel && panel.kind === "file" && (
        <AddFilePanel job={job} file={panel.file} isPhoto={panel.isPhoto} onClose={closePanel} onDone={() => { closeIf("file"); load(); }} />
      )}
      {panel && panel.kind === "video" && <TaskVideoPanel task={panel.task} cameraConfig={cameraConfig} onClose={closePanel} />}
    </div>
  );
}
